import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { LoginServerService } from '../../services/login-server.service';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css']
})
export class ResetPasswordComponent {

  @Input() isVisible: boolean = false;
  @Output() handleVisible: EventEmitter<any> = new EventEmitter<any>();
  @Output() resetComplete: EventEmitter<void> = new EventEmitter<void>();

  step: 1 | 2 | 3 = 1;
  loading = false;
  passwordVisible = false;
  userEmail = '';
  code = '';
  codeDigits: string[] = ['', '', '', '', '', ''];
  codeError = '';
  resendCooldown = 0;
  private resendTimer: any;

  emailForm!: FormGroup;
  passwordForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private notification: NzNotificationService,
    private authProvider: LoginServerService,
  ) {
    this.emailForm = this.fb.group({
      userEmail: ['', [Validators.required, Validators.email]]
    });
    this.passwordForm = this.fb.group({
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]]
    });
  }

  handleCancel(): void {
    this.resetForm();
    this.handleVisible.emit();
  }

  resetForm(): void {
    this.step = 1;
    this.codeDigits = ['', '', '', '', '', ''];
    this.codeError = '';
    this.userEmail = '';
    this.code = '';
    this.loading = false;
    this.resendCooldown = 0;
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.emailForm.reset();
    this.passwordForm.reset();
  }

  submitEmail(): void {
    if (this.emailForm.invalid) {
      Object.values(this.emailForm.controls).forEach(c => { c.markAsDirty(); c.updateValueAndValidity({ onlySelf: true }); });
      return;
    }
    this.loading = true;
    this.userEmail = this.emailForm.value.userEmail;

    this.authProvider.generateResetPasswordToken(this.userEmail).subscribe({
      next: () => {
        this.loading = false;
        this.step = 2;
        this.codeDigits = ['', '', '', '', '', ''];
        this.startResendCooldown();
        this.notification.create('success', 'Code envoyé', 'Un code à 6 chiffres a été envoyé à votre adresse email.', { nzPlacement: 'bottomLeft' });
      },
      error: (err) => {
        this.loading = false;
        const msg = err.error?.message || 'Email non trouvé.';
        this.notification.create('error', 'Erreur', msg, { nzPlacement: 'bottomLeft' });
      }
    });
  }

  startResendCooldown(): void {
    this.resendCooldown = 60;
    if (this.resendTimer) clearInterval(this.resendTimer);
    this.resendTimer = setInterval(() => {
      this.resendCooldown--;
      if (this.resendCooldown <= 0) {
        clearInterval(this.resendTimer);
      }
    }, 1000);
  }

  resendCode(): void {
    if (this.resendCooldown > 0) return;
    this.loading = true;
    this.authProvider.generateResetPasswordToken(this.userEmail).subscribe({
      next: () => {
        this.loading = false;
        this.startResendCooldown();
        this.notification.create('success', 'Code renvoyé', 'Un nouveau code a été envoyé.', { nzPlacement: 'bottomLeft' });
      },
      error: () => { this.loading = false; }
    });
  }

  onCodeInput(event: any, index: number): void {
    const val = event.target.value;
    if (val && /^\d$/.test(val)) {
      this.codeDigits[index] = val;
      if (index < 5) {
        const next = document.querySelector(`.code-input-${index + 1}`) as HTMLInputElement;
        if (next) next.focus();
      }
    } else {
      this.codeDigits[index] = '';
      event.target.value = '';
    }
    this.codeError = '';
  }

  onCodeKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace' && !this.codeDigits[index] && index > 0) {
      const prev = document.querySelector(`.code-input-${index - 1}`) as HTMLInputElement;
      if (prev) {
        prev.focus();
        this.codeDigits[index - 1] = '';
      }
    }
  }

  onCodePaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const digits = pasted.replace(/\D/g, '').slice(0, 6).split('');
    digits.forEach((d, i) => { this.codeDigits[i] = d; });
    const inputs = document.querySelectorAll('[class^="code-input-"]');
    const focusIdx = Math.min(digits.length, 5);
    (inputs[focusIdx] as HTMLInputElement)?.focus();
  }

  get codeString(): string {
    return this.codeDigits.join('');
  }

  verifyCode(): void {
    const code = this.codeString;
    if (code.length !== 6) {
      this.codeError = 'Veuillez saisir les 6 chiffres du code.';
      return;
    }
    this.loading = true;
    this.authProvider.verifyResetCode(this.userEmail, code).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.valid) {
          this.step = 3;
        } else {
          this.codeError = res.message || 'Code incorrect.';
          this.codeDigits = ['', '', '', '', '', ''];
          const firstInput = document.querySelector('.code-input-0') as HTMLInputElement;
          if (firstInput) firstInput.focus();
        }
      },
      error: () => {
        this.loading = false;
        this.codeError = 'Erreur de vérification.';
      }
    });
  }

  submitNewPassword(): void {
    if (this.passwordForm.invalid) {
      Object.values(this.passwordForm.controls).forEach(c => { c.markAsDirty(); c.updateValueAndValidity({ onlySelf: true }); });
      return;
    }
    const { newPassword, confirmPassword } = this.passwordForm.value;
    if (newPassword !== confirmPassword) {
      this.notification.create('error', 'Erreur', 'Les mots de passe ne correspondent pas.', { nzPlacement: 'bottomLeft' });
      return;
    }
    this.loading = true;
    this.authProvider.resetPasswordWithCode(this.userEmail, this.codeString, newPassword).subscribe({
      next: (res) => {
        this.loading = false;
        if (res.success) {
          this.notification.create('success', 'Succès', 'Votre mot de passe a été réinitialisé avec succès !', { nzPlacement: 'bottomLeft' });
          this.resetComplete.emit();
          this.handleCancel();
        } else {
          this.notification.create('error', 'Erreur', res.message || 'Échec de la réinitialisation.', { nzPlacement: 'bottomLeft' });
        }
      },
      error: () => {
        this.loading = false;
        this.notification.create('error', 'Erreur', 'Une erreur est survenue.', { nzPlacement: 'bottomLeft' });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.resendTimer) clearInterval(this.resendTimer);
  }
}
